import { useQuery } from '@tanstack/react-query';
import { format, subYears } from 'date-fns';
import qs from 'qs';
import apiClient from '@/lib/apiClient';

export interface DynamicStatsApiParams {
  makerOrganizationUuid?: string | string[];
  transactionType?: number;
  status?: string;
  transactionDateFrom?: string;
  transactionDateTo?: string;
  targetCurrency?: string;
}

export interface DynamicStatsData {
  aggregatedAt: string;
  currency: string;
  includesCurrencies: string[];
  datacentersQueried: string[];
  totals: {
    count: number;
    amount: number;
  };
  byCustomer: {
    makerOrganizationUuid: string;
    count: number;
    amount: number;
  }[];
}

// Past Due uses a one-year transaction-date window so the balance is consistent across
// every surface, independent of any view's own date window. Silent filter — not disclosed
// in user-facing copy (we advertise one year of data, though ~14 months exist in the DB).
// Exported so the widget's Past Due deep link sends the exact same startDate the KPI query
// used — deriving it twice risks a one-day drift at timezone/leap-year boundaries.
export const pastDueFromDate = format(subYears(new Date(), 1), 'yyyy-MM-dd');

export const pastDueQueryParams: DynamicStatsApiParams = {
  transactionType: 1,
  status: 'PAST_DUE',
  transactionDateFrom: pastDueFromDate,
};

const fetchDynamicStats = async (params: DynamicStatsApiParams): Promise<DynamicStatsData> => {
  const queryString = qs.stringify(params, { arrayFormat: 'repeat' });
  const response = await apiClient
    .get(`api/invoice-hub/dynamic-stats?${queryString}`)
    .json<{ data: DynamicStatsData }>();
  return response.data;
};

export const useDynamicStats = (params: DynamicStatsApiParams) =>
  useQuery({
    queryKey: ['financeiq-widget-dynamic-stats', params],
    queryFn: () => fetchDynamicStats(params),
    retry: 1,
    staleTime: 1000 * 60 * 60 * 24,
  });
